import { ExternalLink, Mail } from 'lucide-react';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';
import { MonogramPattern } from '../components/Brush';
import { IMG } from '../lib/api';

/**
 * Modulo spento (informativa 14/10/2026): nessun invio, nessun dato raccolto.
 * Al suo posto un indirizzo email (audit 03/11, A3): un mailto non raccoglie dati sul sito.
 */
function B2BForm({ kind, title, email }) {
  const { t } = useLang();
  return (
    <div
      data-testid={`${kind}-form-off`}
      className="grid gap-4 bg-carta-2 p-7 md:p-10"
    >
      <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
      <p className="prosa text-base">{t('common.formOff')}</p>
      <p className="flex items-center gap-2 text-base">
        <Mail size={14} className="text-marsala" />
        {t('common.writeTo')}{' '}
        <a href={`mailto:${email}`} data-testid={`${kind}-email`} className="underline underline-offset-4">
          {email}
        </a>
      </p>
    </div>
  );
}

export function Reseller() {
  const { t } = useLang();
  return (
    <>
      <Seo title={t('reseller.title')} description={t('reseller.lead')} path="/professionisti/rivenditori" />

      <header className="relative overflow-hidden bg-inchiostro py-14 text-carta md:py-24">
        <MonogramPattern className="absolute inset-0" opacity={0.06} />
        <div className="wrap relative">
          <h1 className="display-xl max-w-[16ch]">{t('reseller.title')}</h1>
          <p className="prosa mt-8 max-w-[56ch] border-t-2 border-carta/40 pt-6 text-lg !text-carta/75">
            {t('reseller.lead')}
          </p>
        </div>
      </header>

      <section className="py-14 md:py-20">
        <div className="wrap">
          <Stagger className="grid gap-px bg-inchiostro/12 md:grid-cols-2" gap={0.07}>
            {t('reseller.why').map(([title, text]) => (
              <StaggerItem key={title} className="bg-carta p-7 md:p-11">
                <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
                <p className="prosa mt-3">{text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-carta-2 py-12 md:py-16">
        <div className="wrap grid gap-8 md:grid-cols-[1.2fr_auto] md:items-center">
          <div>
            <h2 className="text-3xl md:text-4xl">{t('reseller.faireTitle')}</h2>
            <p className="prosa mt-4">{t('reseller.faireText')}</p>
          </div>
          <a
            href="https://www.faire.com/brand/b_xczuwh5rxj"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faire-cta"
            className="btn btn-solid shrink-0"
          >
            {t('reseller.faireCta')}
            <ExternalLink size={14} strokeWidth={2} />
          </a>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <Reveal>
            <div className="aspect-[4/5] overflow-hidden bg-carta-2">
              <img
                src={IMG.tableware}
                alt="Corner VdE allestito in un punto vendita"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="prosa mb-6">{t('reseller.faireOr')}</p>
            <B2BForm kind="reseller" title={t('reseller.formTitle')} email="commerciale@vde.it" />
          </Reveal>
        </div>
      </section>
    </>
  );
}

/** Un caso alla volta: chi ci ha già scelto, non un elenco di feature. */
export function HorecaCases({ cases, title }) {
  if (!cases?.length) return null;
  return (
    <section data-testid="horeca-cases" className="py-14 md:py-20">
      <div className="wrap">
        <h2 className="font-display text-3xl md:text-4xl">{title}</h2>
        <Stagger className="mt-10 grid gap-px bg-inchiostro/12 sm:grid-cols-2" gap={0.07}>
          {cases.map(({ name, quote, image }) => (
            <StaggerItem key={name} className="bg-carta p-7 md:p-10">
              {image && (
                <div className="mb-6 aspect-[4/3] overflow-hidden bg-carta-2">
                  <img src={image} alt={name} loading="lazy" className="h-full w-full object-cover" />
                </div>
              )}
              <p className="font-display text-xl italic leading-snug">«{quote}»</p>
              <p className="label-wide mt-4 text-marsala">{name}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function Horeca() {
  const { t } = useLang();
  return (
    <>
      <Seo title={t('reseller.horecaTitle')} description={t('reseller.horecaLead')} path="/professionisti/horeca" />

      <header className="py-14 md:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[1.05fr_1fr] lg:items-end lg:gap-20">
          <div>
            <h1 className="display-xl">{t('reseller.horecaTitle')}</h1>
            <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">
              {t('reseller.horecaLead')}
            </p>
          </div>
          <div className="aspect-[4/3] overflow-hidden bg-carta-2">
            <img
              src={IMG.horeca}
              alt="Tavola apparecchiata con piatti e ciotole in gres, calici di vino rosso e candele"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </header>

      <section className="bg-carta-2 py-14 md:py-20">
        <div className="wrap">
          <p className="accento mb-10 text-2xl text-inchiostro/85">Horeca White · Black · Ivory · Stone · Brown</p>
          <div className="grid gap-px bg-inchiostro/12 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Servizio continuo', 'Forme e materiali pensati per resistere a centinaia di coperti al giorno.'],
              ['Impilabile', 'Ottimizzato per lo stoccaggio: meno spazio, meno rotture.'],
              ['Riordinabile', 'Referenze continuative: gli stessi pezzi si riordinano nel tempo.'],
              ['Combinabile', 'Sta bene accanto alle collezioni decorate.'],
            ].map(([title, text]) => (
              <div key={title} className="bg-carta-2 p-6 md:p-8">
                <h2 className="font-display text-2xl">{title}</h2>
                <p className="prosa mt-3">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HorecaCases cases={t('reseller.horecaCases')} title={t('reseller.horecaCasesTitle')} />

      <section className="py-14 md:py-20">
        <div className="wrap max-w-3xl">
          <B2BForm kind="horeca" title={t('reseller.horecaFormTitle')} email="horeca@galileospa.com" />
        </div>
      </section>
    </>
  );
}
